import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {
  ICadGoodResponse,
  ICadGoodsListResponse,
  ICadInvoiceListResponse,
  ICadInvoiceResponse,
  ICargoListResponse,
  IGetDeclarationImporterBrokerInfoResponse,
  IGetDeclarationResponse,
  IWithdrawDeclarationRequest,
  IConvertB3Response,
  IGetGoodsResponse,
  ICADErrorsResponse,
  ICanEditDeclarationResponse,
} from '../models/api';
import { IBaseResponse } from '@carm/_utils/models';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class DeclarationsService extends BaseApiService {
  constructor(protected injector: Injector) {
    super(injector);
  }

  private get baseCadDeclarationUrlBP(): string {
    return `${this.endPointBP}/cad/declaration`;
  }

  convertB3<T>(transactionId: T): Observable<IConvertB3Response> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/b3/${transactionId}/convert`;
    return this.httpAuthPost<IConvertB3Response>(endPoint, {});
  }

  getDeclaration(declarationUid: string): Observable<IGetDeclarationResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}`;
    return this.httpAuthGet<IGetDeclarationImporterBrokerInfoResponse>(
      endPoint,
      null
    ).pipe(
      map(response => ({
        ...response?.declaration,
        importerBrokerInfo: response?.importerBrokerInfo,
      }))
    );
  }

  canEditDeclaration(caseGuid: string): Observable<ICanEditDeclarationResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/${caseGuid}/edit-allowed`;
    return this.httpAuthGet<ICanEditDeclarationResponse>(endPoint);
  }

  getGoodsByDeclarationUid(
    declarationUid: string,
    currentPage: number,
    pageSize: number,
    searchString: string = '',
    sort: string
  ): Observable<IGetGoodsResponse> {
    const params = new HttpParams()
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('pageSize', pageSize.toString())
      .set('searchString', searchString);
    // .set('sort', sort);
    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}/goods`;
    return this.httpAuthGet<IGetGoodsResponse>(endPoint, params, LoaderSpinnerTypes.None);
  }

  getCargoList(declarationUid: string): Observable<ICargoListResponse> {
    const maxPageSize = 250;
    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}/cargo?pageSize=${maxPageSize}`;
    return this.httpAuthGet<ICargoListResponse>(endPoint, null).pipe(map(this.filterEmptyCargo));
  }

  getDeclarationInvoices(
    declarationUid: string,
    currentPage: number,
    pageSize: number,
    searchString: string = ''
  ): Observable<ICadInvoiceListResponse> {
    const params = new HttpParams()
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('pageSize', pageSize.toString())
      .set('searchString', searchString);

    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}/invoice`;
    return this.httpAuthGet<ICadInvoiceListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  getDeclarationInvoiceGoods(
    invoiceUid: string,
    currentPage: number,
    pageSize: number,
    searchString: string = ''
  ): Observable<ICadGoodsListResponse> {
    const params = new HttpParams()
      .set('currentPage', currentPage ? currentPage.toString() : '1')
      .set('pageSize', pageSize.toString())
      .set('searchString', searchString);

    const endPoint = `${this.baseCadDeclarationUrlBP}/invoice/${invoiceUid}/good`;
    return this.httpAuthGet<ICadGoodsListResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  getFirstVersionDeclaration(transactionNo: string): Observable<IGetDeclarationResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/transaction/${transactionNo}/baseVersion`;
    return this.httpAuthGet<IGetDeclarationResponse>(
      endPoint
    );
  }

  withdrawDeclaration(
    declarationId: string,
    withdrawDeclaration: IWithdrawDeclarationRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/withdrawal/${declarationId}`;
    return this.httpAuthPost<IBaseResponse>(endPoint, withdrawDeclaration);
  }

  withdrawRoi(
    roiDraftUid: string,
    withdrawDeclaration: IWithdrawDeclarationRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.endPointBP}/cad/roi/${roiDraftUid}`;
    const payload = { notes: withdrawDeclaration.notes };
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, payload);
  }

  transferDeclaration(
    declarationId: string,
    withdrawDeclaration: IWithdrawDeclarationRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/transfer/${declarationId}`;
    return this.httpAuthPost<IBaseResponse>(endPoint, withdrawDeclaration);
  }

  transferDeclarationToAnotherBusiness(
    declarationId: string,
    withdrawDeclaration: IWithdrawDeclarationRequest
  ): Observable<IBaseResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/transfer/swap-business/${declarationId}`;
    return this.httpAuthPost<IBaseResponse>(endPoint, withdrawDeclaration);
  }

  generateVCTransactionNumber(
    declarationType: string,
    officeCode: string
  ): Observable<string> {
    const body = {
      declarationTypeCode: declarationType,
      cbsaOfficeCode: officeCode,
    };
    const endPoint = `${this.baseCadDeclarationUrlBP}/vctransaction/`;
    return this.httpAuthPost<IBaseResponse>(endPoint, body).pipe(
      map((response: IBaseResponse) => response.responseUid)
    );
  }

  validateVCTransactionNumber(
    transactionNum: string,
    declarationType: string,
    officeCode: string
  ): Observable<IBaseResponse> {
    const body = {
      declarationTypeCode: declarationType,
      transactionNumber: transactionNum,
      cbsaOfficeCode: officeCode,
    };
    const endPoint = `${this.baseCadDeclarationUrlBP}/vctransaction/validate`;
    return this.httpAuthPost<IBaseResponse>(endPoint, body);
  }

  getInvoice(invoiceUid: string): Observable<ICadInvoiceResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/invoice/${invoiceUid}`;
    return this.httpAuthGet<ICadInvoiceResponse>(endPoint);
  }

  getGood(goodUid: string): Observable<ICadGoodResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/good/${goodUid}`;
    return this.httpAuthGet<ICadGoodResponse>(endPoint);
  }

  getGoodByLineNumber(
    declarationUid: string,
    goodLineNo: string
  ): Observable<ICadGoodResponse> {
    const params = new HttpParams()
      .set('currentPage', '1')
      .set('pageSize', '10')
      .set('goodLineNo', goodLineNo);

    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}/goods`;
    return this.httpAuthGet<ICadGoodsListResponse>(endPoint, params).pipe(
      map(g =>
        g.results?.length > 0
          ? g.results.find(r => r.goodLineNo === parseInt(goodLineNo, 10))
          : null
      )
    ); // returns a list of 1.
  }

  downloadDeclaration(declarationUid: string): Observable<Blob> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/${declarationUid}/pdf`;
    return this.httpAuthDownloadFile(endPoint);
  }

  startAdjust(
    declarationId: string,
    transactionNo: string
  ): Observable<IBaseResponse> {
    const endPoint = `${this.baseCadDeclarationUrlBP}/edit/${declarationId}/transaction/${transactionNo}`;
    return this.httpAuthPost<IBaseResponse>(endPoint, null);
  }

  getGoodsByTransactionNumbers(
    transactionNumbers: string[],
    currentPage: number,
    pageSize: number,
    searchString: string,
    sort = 'GOOD_LINE_NUMBER_ASC'
  ): Observable<ICadGoodsListResponse> {
    const data = { uids: transactionNumbers };

    const endPoint = `${this.baseCadDeclarationUrlBP}/goods?currentPage=${currentPage}&pageSize=${pageSize}${searchString ? `&searchString=${searchString}` : ``}&sort=${sort}`;
    return this.httpAuthPost<ICadGoodsListResponse>(endPoint, data, null, LoaderSpinnerTypes.AgGrid);
  }

  getInvoicesByTransactionNumbers(
    transactionNumbers: string[],
    currentPage: number,
    pageSize: number,
    searchString: string,
    sort = 'INVOICE_LINE_NUMBER_ASC'
  ): Observable<ICadInvoiceListResponse> {
    const data = { uids: transactionNumbers };
    const endPoint = `${this.baseCadDeclarationUrlBP}/invoices?currentPage=${currentPage}&pageSize=${pageSize}${searchString ? `&searchString=${searchString}` : ``}&sort=${sort}`;
    return this.httpAuthPost<ICadInvoiceListResponse>(endPoint, data);
  }

  getErrors(declarationUid: string): Observable<ICADErrorsResponse> {
    const endPoint = `${this.endPointBP}/cad/dutydrawback/${declarationUid}/errors`;
    return this.httpAuthGet<ICADErrorsResponse>(endPoint);
  }

  getCADErrors(declarationUid: string): Observable<ICADErrorsResponse> {
    const endPoint = `${this.endPointBP}/cad/declaration/${declarationUid}/errors`;
    return this.httpAuthGet<ICADErrorsResponse>(endPoint, null, LoaderSpinnerTypes.AgGrid);
  }

  filterEmptyCargo(cargoResponse:ICargoListResponse): ICargoListResponse{
    var cargoList =  cargoResponse.results.filter(c=>c.cargoControlNo !== 'N/A');
    cargoResponse.results = cargoList;
    return cargoResponse;
 }
}
